import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaVehicleRegistrationGuidePage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Vehicle Registration Guide</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Vehicle Registration Guide</h1>
        <p className="text-obsidian-300">Complete guide to registering your vehicle in Nevada — requirements, fees, and step-by-step process.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="prose prose-sm max-w-none text-obsidian-700 space-y-8">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Overview: Vehicle Registration in Nevada</h2>
          <p>Nevada law requires all vehicles operated on public roads to be registered with the Nevada Department of Motor Vehicles (DMV). New residents must register their vehicles within <strong>30 days</strong> of establishing Nevada residency. Newly purchased vehicles must also be registered within 30 days of purchase.</p>
          <p className="mt-3">Speedy DMV Services in Las Vegas can handle your vehicle registration quickly and professionally, saving you the time and hassle of visiting a DMV office.</p>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Required Documents for Vehicle Registration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: 'ri-file-text-line', title: 'Proof of Ownership', desc: 'Vehicle title or security agreement (if financed)' },
              { icon: 'ri-shield-check-line', title: 'Nevada Insurance', desc: 'Must be issued by a Nevada-licensed agent in the same name(s) as the registration' },
              { icon: 'ri-leaf-line', title: 'Emissions Report', desc: 'Required for most vehicles in Clark and Washoe counties if last test is over 90 days old' },
              { icon: 'ri-search-eye-line', title: 'VIN Inspection', desc: 'Required for vehicles never before registered in Nevada ($1 fee)' },
              { icon: 'ri-speed-line', title: 'Odometer Reading', desc: 'Required for most vehicles (except motorcycles and mopeds)' },
              { icon: 'ri-money-dollar-circle-line', title: 'Payment', desc: 'Registration fees, governmental services tax, and any applicable penalties' },
            ].map(item => (
              <div key={item.title} className="flex items-start space-x-3 p-4 border border-obsidian-100 rounded-lg">
                <div className="w-8 h-8 flex items-center justify-center bg-gold-50 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-gold-600`}></i>
                </div>
                <div>
                  <p className="font-semibold text-obsidian-900 text-sm">{item.title}</p>
                  <p className="text-obsidian-500 text-xs mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Registration Fees</h2>
          <div className="bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-obsidian-200">
                  <th className="text-left py-2 font-semibold text-obsidian-900">Fee Type</th>
                  <th className="text-right py-2 font-semibold text-obsidian-900">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-obsidian-100">
                {[
                  ['Base Registration Fee (passenger cars)', '$33.00'],
                  ['Governmental Services Tax (GST)', 'Based on MSRP & vehicle age'],
                  ['VIN Inspection (if required)', '$1.00'],
                  ['Smog Certificate (if required)', 'Varies by station'],
                  ['Late Penalty', '$6.00 per month'],
                  ['Tax Penalty', '10% of past-due GST ($6 min)'],
                ].map(([label, amount]) => (
                  <tr key={label}>
                    <td className="py-2.5 text-obsidian-700">{label}</td>
                    <td className="py-2.5 text-right font-medium text-obsidian-900">{amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-obsidian-400 mt-3">* Fees are subject to change. Always verify current fees at dmv.nv.gov or by calling the DMV.</p>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Step-by-Step Registration Process</h2>
          <ol className="space-y-4">
            {[
              { step: '1', title: 'Gather Required Documents', desc: 'Collect your title, proof of Nevada insurance, emissions report (if applicable), and payment.' },
              { step: '2', title: 'Complete VIN Inspection (if needed)', desc: 'Vehicles never before registered in Nevada require a VIN inspection by a DMV rep or peace officer.' },
              { step: '3', title: 'Pass Emissions Test (if required)', desc: 'Most vehicles in Clark County (Las Vegas) require a smog check. Find an authorized station near you.' },
              { step: '4', title: 'Submit Application', desc: 'Visit a Nevada DMV office, use an authorized third-party provider like Speedy DMV Services, or submit by mail for eligible transactions.' },
              { step: '5', title: 'Pay Fees', desc: 'Pay all applicable registration fees, governmental services tax, and any penalties.' },
              { step: '6', title: 'Receive Registration & Plates', desc: 'You\'ll receive your registration certificate and license plates (for new registrations) or a renewal sticker.' },
            ].map(item => (
              <li key={item.step} className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-obsidian-900 text-gold-400 rounded-full font-bold text-sm flex-shrink-0">{item.step}</div>
                <div>
                  <p className="font-semibold text-obsidian-900 text-sm">{item.title}</p>
                  <p className="text-obsidian-500 text-xs mt-0.5">{item.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Let Us Handle Your Registration</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services handles all vehicle registration paperwork in Las Vegas. No DMV lines, no hassle.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaVehicleRegistrationGuidePage;
